import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { db } from '../../services/db';
import SafeIcon from '../../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalScores: 0,
    avgScore: 0,
    totalCalculator: 0,
    totalNewsletter: 0,
    totalLeads: 0,
    weekScores: 0,
    weekCalculator: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    const scores = await db.getScores();
    const calculator = await db.getCalculatorResults();
    const newsletter = await db.getNewsletterSubscribers();
    const leads = await db.getLeads();

    const now = Date.now();
    const weekAgo = now - (7 * 24 * 60 * 60 * 1000);

    const weekScores = scores.filter(s => new Date(s.date).getTime() > weekAgo).length;
    const weekCalculator = calculator.filter(c => new Date(c.date).getTime() > weekAgo).length;

    const avgScore = scores.length > 0 
      ? Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length)
      : 0;

    setStats({
      totalScores: scores.length,
      avgScore,
      totalCalculator: calculator.length,
      totalNewsletter: newsletter.length,
      totalLeads: leads.length,
      weekScores,
      weekCalculator
    });

    // Recent activity
    const allActivity = [
      ...scores.map(s => ({ type: 'scorecard', email: s.email, date: s.date })),
      ...calculator.map(c => ({ type: 'calculator', email: c.email, date: c.date })),
      ...newsletter.map(n => ({ type: 'newsletter', email: n.email, date: n.date }))
    ];

    allActivity.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setRecentActivity(allActivity.slice(0, 10));
  };

  const statCards = [
    {
      label: 'Scorecard Completions',
      value: stats.totalScores,
      subtext: `+${stats.weekScores} this week`,
      icon: FiIcons.FiCheckCircle,
      color: 'pink'
    },
    {
      label: 'Average Score',
      value: `${stats.avgScore}/100`,
      subtext: 'Overall performance',
      icon: FiIcons.FiTrendingUp,
      color: 'blue'
    },
    {
      label: 'Calculator Uses',
      value: stats.totalCalculator,
      subtext: `+${stats.weekCalculator} this week`,
      icon: FiIcons.FiDollarSign,
      color: 'green'
    },
    {
      label: 'Newsletter Subscribers',
      value: stats.totalNewsletter,
      subtext: 'Total subscribers',
      icon: FiIcons.FiMail,
      color: 'purple'
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-${stat.color}-100`}>
                  <SafeIcon icon={stat.icon} className={`text-2xl text-${stat.color}-600`} />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
              <p className="text-xs text-gray-400">{stat.subtext}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin/events"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-pink-300 hover:bg-pink-50 transition-all"
            >
              <SafeIcon icon={FiIcons.FiPlus} className="text-pink-600 text-xl" />
              <span className="font-medium text-gray-700">Add New Event</span>
            </Link>
            <Link
              to="/admin/resources"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-pink-300 hover:bg-pink-50 transition-all"
            >
              <SafeIcon icon={FiIcons.FiUpload} className="text-pink-600 text-xl" />
              <span className="font-medium text-gray-700">Upload Resource</span>
            </Link>
            <Link
              to="/admin/leads"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-pink-300 hover:bg-pink-50 transition-all"
            >
              <SafeIcon icon={FiIcons.FiDownload} className="text-pink-600 text-xl" />
              <span className="font-medium text-gray-700">Export All Leads</span>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentActivity.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                No recent activity
              </div>
            ) : (
              recentActivity.map((activity, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    activity.type === 'scorecard' ? 'bg-pink-100' :
                    activity.type === 'calculator' ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    <SafeIcon 
                      icon={
                        activity.type === 'scorecard' ? FiIcons.FiCheckCircle :
                        activity.type === 'calculator' ? FiIcons.FiDollarSign : FiIcons.FiMail
                      }
                      className={`${
                        activity.type === 'scorecard' ? 'text-pink-600' :
                        activity.type === 'calculator' ? 'text-green-600' : 'text-purple-600'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.email}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      {activity.type === 'scorecard' ? 'Completed Scorecard' :
                       activity.type === 'calculator' ? 'Used Calculator' : 'Subscribed to Newsletter'}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(activity.date).toLocaleDateString()}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;